// PHASE 11C-1 CORRECTION (route-producer canonicalization).
//
// ONE shared, narrowly-scoped, pure route builder for the two chat route
// shapes ("/chat/:chatId", "/chat/new/:otherUid") - every production
// navigate()/href producer that constructs a chat route must go through
// this module instead of hand-interpolating a template literal (see
// PROVEN DEFECT below).
//
// PROVEN DEFECT this closes: every real producer (UserProfilePage.js's
// own `navigate(\`/chat/${chatId}\`)`, MessagesPage.js's own
// `navigate(\`/chat/${chatId}\`, ...)`, NotificationsPage.js's own deep-
// link path builder) previously interpolated the LOGICAL chat id directly,
// with NO encoding at all. For a logical id containing a literal "%" -
// which this app's own audited UID/document-id contract explicitly
// permits (see nativeChatProfileUidSegment.js's own doc comment) - this
// produced an UNSAFE raw route the native strict decoder cannot parse:
//   logical:      "alice_user%name"
//   unsafe route: "/chat/alice_user%name"   <- native rejects: "%na" is
//                                               not a valid two-hex-digit
//                                               percent sequence, so the
//                                               whole route fails closed
//                                               and falls back to web.
//   safe route:   "/chat/alice_user%25name" <- encodeURIComponent turns
//                                               the literal "%" into
//                                               "%25" - native decodes
//                                               this ONE percent triple
//                                               back to the literal "%",
//                                               recovering the exact
//                                               original logical id.
// Similarly, a logical id containing the literal text "%2e" must become
// "%252e" (never "%2e" left bare) so that native's own single decode
// recovers the literal "%2e" - never "." (which "%2e" alone would
// decode to, and native's own dot-segment rejection would then close).
//
// INPUT CONTRACT: every function below accepts ONLY an already-decoded
// LOGICAL identifier - never an already-encoded path segment, never a
// full route, never a full URL. A logical uid whose own characters
// happen to spell out "%C3%A9" is valid, literal DATA (nine ordinary
// characters, one of them "%") - it is encoded to "%25C3%25A9" and is
// NEVER reinterpreted as the single character "é". Callers must never
// pre-encode a value before passing it here - doing so would double-
// encode it.
//
// This module only ever ENCODES (exactly once, only the final path
// segment - never the whole route) - it never decodes anything.
//
// CANONICAL ENCODED-SEGMENT GRAMMAR (PHASE 11C-1 CORRECTION, canonical
// encoded-chat-segment closure): plain `encodeURIComponent` alone is NOT
// byte-identical to NativeChatWebRouteBuilder.java's own output - it
// leaves nine characters raw ("-", "_", ".", "!", "~", "*", "'", "(",
// ")"), proven empirically (see this module's own test file). The
// canonical grammar this feature actually commits to is STRICTER: the
// ONLY raw (never-encoded) bytes are `[A-Za-z0-9_-]` - every other byte,
// including the other seven `encodeURIComponent` leaves bare, is always
// percent-encoded as exactly two UPPERCASE hex digits. This exactly
// matches RouteAllowList.java's own ENCODED_CHAT_ID_SEGMENT grammar
// ("[A-Za-z0-9_-]" raw, or a well-formed "%XX" triple) and
// NativeChatWebRouteBuilder.java's own encoder - see
// encodeCanonicalChatPathSegment below, the JS half of that same
// contract.

import { isSafeOpaqueUidSegment } from "./nativeChatProfileUidSegment";

// The seven characters encodeURIComponent leaves raw that the canonical
// grammar does NOT permit raw (encodeURIComponent already leaves "-" and
// "_" raw, which the canonical grammar also permits raw, so those two are
// never in this list).
const CHARACTERS_ENCODE_URI_COMPONENT_LEAVES_RAW_BUT_CANONICAL_GRAMMAR_FORBIDS_RAW = /[.!~*'()]/g;

function toUppercasePercentByte(character) {
  return "%" + character.charCodeAt(0).toString(16).toUpperCase();
}

/**
 * Encodes `logical` as ONE canonical chat-route path segment: the only
 * raw bytes ever left un-encoded are `[A-Za-z0-9_-]` - every other UTF-8
 * byte (including the seven `encodeURIComponent` itself leaves bare) is
 * percent-encoded as exactly two UPPERCASE hex digits. Byte-identical to
 * NativeChatWebRouteBuilder.java's own output for the same logical input
 * - see the printable-ASCII cross-language fixture matrix proving this.
 */
export function encodeCanonicalChatPathSegment(logical) {
  return encodeURIComponent(logical).replace(
    CHARACTERS_ENCODE_URI_COMPONENT_LEAVES_RAW_BUT_CANONICAL_GRAMMAR_FORBIDS_RAW,
    toUppercasePercentByte
  );
}

// A composite chat document id is at most two full-length (128) Firebase
// Authentication uids joined by exactly one "_" - mirrors
// ChatRouteParser.java's own MAX_LOGICAL_CHAT_DOCUMENT_ID_LENGTH exactly.
const MAX_LOGICAL_CHAT_DOCUMENT_ID_LENGTH = 128 * 2 + 1;

/**
 * Validates a logical canonical chat document id using the SAME
 * character-safety rules as a single uid (see
 * nativeChatProfileUidSegment.js's own isSafeOpaqueUidSegment), but with
 * the longer composite-id bound - a dedicated check, never the single-uid
 * one, exactly mirroring ChatRouteParser.java's own
 * isValidLogicalChatDocumentId/isValidUnicodeSafeNewRouteId split.
 */
function isValidLogicalChatDocumentId(value) {
  return isSafeOpaqueUidSegment(value, MAX_LOGICAL_CHAT_DOCUMENT_ID_LENGTH);
}

/**
 * Builds the "/chat/:chatId" (Mode.EXISTING) route for an already-
 * computed, already-decoded logical canonical chat document id (e.g. the
 * output of ChatRoomPage.js's own getChatId).
 *
 * @param {unknown} logicalCanonicalChatId
 * @returns {string|null} the route, or null if the input is not a safe
 *   logical document id - callers must never navigate on a null result.
 */
export function buildExistingChatRoute(logicalCanonicalChatId) {
  if (!isValidLogicalChatDocumentId(logicalCanonicalChatId)) {
    return null;
  }
  return `/chat/${encodeCanonicalChatPathSegment(logicalCanonicalChatId)}`;
}

/**
 * Builds the "/chat/new/:otherUid" (Mode.NEW) route for an already-
 * decoded logical proposed other-participant uid.
 *
 * @param {unknown} logicalOtherUid
 * @returns {string|null} the route, or null if the input is not a safe
 *   logical uid - callers must never navigate on a null result.
 */
export function buildNewChatRoute(logicalOtherUid) {
  if (!isSafeOpaqueUidSegment(logicalOtherUid)) {
    return null;
  }
  return `/chat/new/${encodeCanonicalChatPathSegment(logicalOtherUid)}`;
}
